'use strict';

const libPictProvider = require('pict-provider');
const libUserSearch = require('../access/User-Search.js');

/**
 * ActivityObservabilityProvider — the shared data+render engine behind the three bespoke observability views
 * (User Activity, User Logins, Project Activity). One provider so the temporal-count engine, the date bucketizer,
 * the REST helpers and the (theme-aware) chart plumbing live in one place; each thin view (SuperUserGrant-style)
 * delegates its render into a mount div here.
 *
 * SCALE: never pull entity rows to count them. Every temporal series is built from server-side
 * `{Entity}s/Count/FilteredTo/<filter>` calls (SQL COUNT(*), zero row transfer, no page cap) — one per
 * (entity, time bucket), run under bounded concurrency. Validated against LADOTD (11.4M observations). The only
 * row reads are bounded lists: the login roster (paged/searched) and the project recent-activity stream (a small
 * capped tail read per entity).
 *
 * TENANCY: customer-scoped via callerCustomer() (the session's CustomerID); the server stays authoritative. The
 * views work for customer admins directly and for a super-user who has impersonated a customer. Cross-customer
 * widening without impersonation is intentionally not attempted here (server RLS scopes to the session customer).
 */
class ActivityObservabilityProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;

		// Per-view state kept apart so the three surfaces don't stomp each other.
		this.userActivity =
		{
			IDUser: null, User: null, Window: '90d', Granularity: 'auto', Metric: 'create',
			Loading: false, Error: '', Buckets: [], Series: [], Totals: {},
			Progress: { active: false, done: 0, total: 0, perEntity: [] },
			PickerOpen: false, PickerSearch: '', PickerResults: [], PickerSearching: false, Token: 0,
		};
		this.logins =
		{
			Loading: false, Error: '', Users: [], Filter: '', Total: 0,
			Recency: { active: 0, idle: 0, dormant: 0, never: 0 },
			Searching: false, SearchResults: null, SearchTerm: '', Token: 0,
		};
		this.projectActivity =
		{
			IDProject: null, Project: null, Window: '90d', Granularity: 'auto', Metric: 'create',
			Loading: false, Error: '', Buckets: [], Series: [], Totals: {}, Stream: [], StreamLoading: false,
			Progress: { active: false, done: 0, total: 0, perEntity: [] }, PickerOpen: false,
			PickerSearch: '', PickerResults: [], PickerSearching: false, Token: 0, PickerPage: 0, PickerHasMore: false,
		};

		this._userNameCache = {};
		this._pickerTimer = null;
	}

	// ---- shared plumbing -----------------------------------------------------------------------------

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	get app() { return this.pict.PictApplication; }
	get chartProvider() { return this.pict.providers.PlatformDashboardData; }

	/** Fable's built-in progress/time-estimation service (ProgressTrackerSet) — lazily instantiated once. */
	get trackerSet()
	{
		if (this._trackerSet === undefined)
		{
			try { this._trackerSet = this.pict.fable.instantiateServiceProviderIfNotExists('ProgressTrackerSet'); }
			catch (pError) { this._trackerSet = null; }
		}
		return this._trackerSet;
	}

	/** Trim Fable's formatTimeDuration ("3s 421ms") to its coarse head ("3s") once a second-or-larger unit is present. */
	_coarseDuration(pText)
	{
		const tmpText = String(pText == null ? '' : pText);
		const tmpStripped = tmpText.replace(/\s*\d+ms$/, '').trim();
		return tmpStripped.length ? tmpStripped : tmpText;
	}

	/** The session customer; all queries scope to it (server stays authoritative). */
	callerCustomer() { return (this.pict.AppData && this.pict.AppData.CurrentUser && this.pict.AppData.CurrentUser.CustomerID) || null; }
	sessionUserID() { return (this.pict.AppData && this.pict.AppData.CurrentUser && this.pict.AppData.CurrentUser.UserID) || null; }
	isSuperUser() { return !!(this.app && typeof this.app._isSuperUser === 'function' && this.app._isSuperUser()); }

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) => this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody)));
	}

	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }

	_assign(pSelector, pHTML)
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement(pSelector).length) { tmpCA.assignContent(pSelector, pHTML); }
	}

	/** Inject the shared .ao-* stylesheet once. */
	_ensureStyles()
	{
		if (document.getElementById('ao-observability-styles')) { return; }
		const tmpStyle = document.createElement('style');
		tmpStyle.id = 'ao-observability-styles';
		tmpStyle.textContent = ActivityObservabilityProvider.SHARED_CSS;
		document.head.appendChild(tmpStyle);
	}

	// ---- concurrency + counting ---------------------------------------------------------------------

	/** Run fWorker over items with at most pLimit in flight; results keep item order. */
	async _mapLimit(pItems, pLimit, fWorker)
	{
		const tmpResults = new Array(pItems.length);
		let tmpNext = 0;
		const fRunner = async () =>
		{
			while (tmpNext < pItems.length)
			{
				const tmpIndex = tmpNext++;
				tmpResults[tmpIndex] = await fWorker(pItems[tmpIndex], tmpIndex);
			}
		};
		const tmpRunners = [];
		for (let i = 0; i < Math.min(pLimit, pItems.length); i++) { tmpRunners.push(fRunner()); }
		await Promise.all(tmpRunners);
		return tmpResults;
	}

	/** One server-side COUNT(*); returns a number (0 on any error, so a slow/failed entity degrades gracefully). */
	async _countAsync(pEntityPlural, pFilter)
	{
		try
		{
			const tmpBody = await this._getJSON(`${pEntityPlural}/Count/FilteredTo/${pFilter}`);
			if (tmpBody && tmpBody.Count != null) { return Number(tmpBody.Count) || 0; }
			if (typeof tmpBody === 'number') { return tmpBody; }
			return 0;
		}
		catch (pError) { return 0; }
	}

	/**
	 * Count several entities' temporal series through ONE bounded pool (so total concurrency is fixed and the
	 * progress is reportable): one COUNT call per (entity, bucket), ANDing the entity base filter with the
	 * bucket's [start,end) date range. Mutates pProgress ({ active, done, total, perEntity }) and calls
	 * fOnProgress (throttled) as counts land, so the view can show a live "what it's doing / how far" panel.
	 *
	 * @param {Array<{plural:string,label:string,color:string}>} pEntities
	 * @return {Promise<Array<{label:string,color:string,counts:number[],total:number}>>}
	 */
	async _countAllSeriesAsync(pEntities, pBaseStanzas, pDateField, pBuckets, pProgress, fOnProgress)
	{
		pProgress.active = true;
		pProgress.total = pEntities.length * pBuckets.length;
		pProgress.done = 0;
		pProgress.etaText = null;
		pProgress.elapsedText = null;
		pProgress.perEntity = pEntities.map((pEntity) => ({ label: pEntity.label, color: pEntity.color, done: 0, total: pBuckets.length }));
		const tmpResults = pEntities.map(() => new Array(pBuckets.length).fill(0));
		const tmpTasks = [];
		pEntities.forEach((pEntity, pEI) => pBuckets.forEach((pBucket, pBI) => tmpTasks.push({ ei: pEI, bi: pBI, plural: pEntity.plural })));
		// Fable's built-in time estimator: a fresh, uniquely-hashed tracker per run (so a superseded run can't
		// corrupt a newer one's timing), started now and incremented as each count lands.
		const tmpSet = this.trackerSet;
		this._trackerSeq = (this._trackerSeq || 0) + 1;
		const tmpTrackerHash = `ActivityObservability-Counts-${this._trackerSeq}`;
		if (tmpSet) { tmpSet.createProgressTracker(tmpTrackerHash, pProgress.total); tmpSet.startProgressTracker(tmpTrackerHash); }
		if (fOnProgress) { fOnProgress(); }
		await this._mapLimit(tmpTasks, 8, async (pTask) =>
		{
			const tmpBucket = pBuckets[pTask.bi];
			const tmpFilter = `${pBaseStanzas}~FBV~${pDateField}~GE~${tmpBucket.s}~FBV~${pDateField}~LT~${tmpBucket.e}`;
			tmpResults[pTask.ei][pTask.bi] = await this._countAsync(pTask.plural, tmpFilter);
			pProgress.done++;
			pProgress.perEntity[pTask.ei].done++;
			if (tmpSet)
			{
				const tmpStatus = tmpSet.incrementProgressTracker(tmpTrackerHash, 1);
				if (tmpStatus)
				{
					pProgress.etaText = (tmpStatus.EstimatedCompletionTime >= 0) ? this._coarseDuration(tmpSet.progressTimes.formatTimeDuration(tmpStatus.EstimatedCompletionTime)) : null;
					pProgress.elapsedText = (tmpStatus.ElapsedTime >= 0) ? this._coarseDuration(tmpSet.progressTimes.formatTimeDuration(tmpStatus.ElapsedTime)) : null;
				}
			}
			// Update on every completed request — there are only tens of counts, so a per-count repaint of the
			// small progress panel is cheap and gives the user a live, moving indicator.
			if (fOnProgress) { fOnProgress(); }
		});
		if (tmpSet) { tmpSet.endProgressTracker(tmpTrackerHash); }
		pProgress.active = false;
		return pEntities.map((pEntity, pEI) => ({ label: pEntity.label, color: pEntity.color, counts: tmpResults[pEI], total: tmpResults[pEI].reduce((pA, pB) => pA + pB, 0) }));
	}

	/** Centered progress panel: overall bar + a per-record-type row, so a long count shows WHAT it is doing and HOW FAR. */
	_progressHTML(pProgress, pTitle)
	{
		const tmpPct = pProgress.total ? Math.round((100 * pProgress.done) / pProgress.total) : 0;
		const tmpEta = pProgress.etaText
			? ` &middot; ~${this._esc(pProgress.etaText)} left`
			: ((pProgress.done > 0 && pProgress.done < pProgress.total) ? ' &middot; estimating…' : '');
		let tmpHTML = '<div class="ao-progress">';
		tmpHTML += '<div class="ao-progress-spin"><i class="fas fa-circle-notch fa-spin"></i></div>';
		tmpHTML += `<div class="ao-progress-title">${this._esc(pTitle)}</div>`;
		tmpHTML += `<div class="ao-progress-sub">${this._num(pProgress.done)} of ${this._num(pProgress.total)} time-slices counted &middot; ${tmpPct}%${tmpEta}</div>`;
		tmpHTML += `<div class="ao-progress-bar"><div class="ao-progress-fill" style="width:${tmpPct}%"></div></div>`;
		tmpHTML += '<div class="ao-progress-entities">';
		(pProgress.perEntity || []).forEach((pEntity) =>
		{
			const tmpDone = pEntity.done >= pEntity.total;
			const tmpEPct = pEntity.total ? Math.round((100 * pEntity.done) / pEntity.total) : 0;
			tmpHTML += '<div class="ao-progress-entity">'
				+ `<span class="ao-progress-dot" style="background:${pEntity.color}"></span>`
				+ `<span class="ao-progress-elabel">${this._esc(pEntity.label)}</span>`
				+ `<span class="ao-progress-etrack"><span class="ao-progress-efill" style="width:${tmpEPct}%;background:${pEntity.color}"></span></span>`
				+ `<span class="ao-progress-ecount">${tmpDone ? '<i class="fas fa-check"></i>' : `${pEntity.done}/${pEntity.total}`}</span>`
				+ '</div>';
		});
		tmpHTML += '</div></div>';
		return tmpHTML;
	}

	// ---- date bucketizing (UTC, to match stored UTC CreateDate) --------------------------------------

	_iso(pDate) { return pDate.toISOString().slice(0, 19); }

	_utcDay(pDate) { return new Date(Date.UTC(pDate.getUTCFullYear(), pDate.getUTCMonth(), pDate.getUTCDate())); }

	/** Window preset -> {days}; used to compute the start date (end = now). */
	_windowDays(pKey)
	{
		return ({ '7d': 7, '30d': 30, '90d': 90, '12mo': 365 })[pKey] || 90;
	}

	/** Pick a bucket size for a window length when Granularity==='auto'; also the coarsen ladder. */
	_effectiveGranularity(pWindowKey, pGranularity)
	{
		if (pGranularity && pGranularity !== 'auto') { return pGranularity; }
		const tmpDays = this._windowDays(pWindowKey);
		if (tmpDays <= 14) { return 'day'; }
		if (tmpDays <= 120) { return 'week'; }
		return 'month';
	}

	/**
	 * Produce [{ label, s, e }] buckets over the last window, in UTC, capped at ~60 buckets (auto-coarsening
	 * day->week->month) so the count fan-out stays bounded.
	 */
	_buildBuckets(pWindowKey, pGranularity)
	{
		const tmpDays = this._windowDays(pWindowKey);
		let tmpGran = this._effectiveGranularity(pWindowKey, pGranularity);
		const tmpLadder = [ 'day', 'week', 'month' ];
		const tmpNow = new Date();
		const tmpEnd = this._utcDay(tmpNow);
		tmpEnd.setUTCDate(tmpEnd.getUTCDate() + 1); // include today (exclusive upper bound = tomorrow 00:00Z)

		const fBuild = (pGran) =>
		{
			const tmpStart = this._utcDay(new Date(tmpNow.getTime() - (tmpDays * 86400000)));
			const tmpBuckets = [];
			if (pGran === 'month')
			{
				let tmpCursor = new Date(Date.UTC(tmpStart.getUTCFullYear(), tmpStart.getUTCMonth(), 1));
				while (tmpCursor < tmpEnd)
				{
					const tmpNextMonth = new Date(Date.UTC(tmpCursor.getUTCFullYear(), tmpCursor.getUTCMonth() + 1, 1));
					tmpBuckets.push({ label: tmpCursor.toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' }), s: this._iso(tmpCursor), e: this._iso(tmpNextMonth) });
					tmpCursor = tmpNextMonth;
				}
			}
			else
			{
				const tmpStep = (pGran === 'week') ? 7 : 1;
				let tmpCursor = new Date(tmpStart.getTime());
				while (tmpCursor < tmpEnd)
				{
					const tmpNextDate = new Date(tmpCursor.getTime() + (tmpStep * 86400000));
					const tmpLabel = (pGran === 'week')
						? tmpCursor.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', timeZone: 'UTC' })
						: tmpCursor.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', timeZone: 'UTC' });
					tmpBuckets.push({ label: tmpLabel, s: this._iso(tmpCursor), e: this._iso(tmpNextDate) });
					tmpCursor = tmpNextDate;
				}
			}
			return tmpBuckets;
		};

		let tmpResult = fBuild(tmpGran);
		// Auto-coarsen if a fine granularity blew past the cap.
		let tmpLadderIndex = tmpLadder.indexOf(tmpGran);
		while (tmpResult.length > 60 && tmpLadderIndex < tmpLadder.length - 1)
		{
			tmpLadderIndex++;
			tmpGran = tmpLadder[tmpLadderIndex];
			tmpResult = fBuild(tmpGran);
		}
		this._activeGranularity = tmpGran;
		return tmpResult;
	}

	// ---- theme-aware chart helpers ------------------------------------------------------------------

	_themeColors()
	{
		let tmpText = '#1f2733';
		let tmpGrid = 'rgba(120,130,145,0.18)';
		try
		{
			const tmpCS = getComputedStyle(document.documentElement);
			tmpText = (tmpCS.getPropertyValue('--theme-color-text-primary') || '').trim() || tmpText;
			const tmpBorder = (tmpCS.getPropertyValue('--theme-color-border-light') || '').trim();
			if (tmpBorder) { tmpGrid = tmpBorder; }
		}
		catch (pError) { /* getComputedStyle unavailable -> defaults */ }
		return { text: tmpText, grid: tmpGrid };
	}

	get palette()
	{
		return { Observation: '#ef4444', Document: '#3b82f6', Comment: '#22c55e', Approval: '#f59e0b', LineItem: '#8b5cf6', Update: '#94a3b8' };
	}

	_stackedBarOptions()
	{
		const tmpTheme = this._themeColors();
		return {
			plugins: { legend: { position: 'bottom', labels: { color: tmpTheme.text, usePointStyle: true, boxWidth: 10 } }, tooltip: { mode: 'index', intersect: false } },
			scales:
			{
				x: { stacked: true, ticks: { color: tmpTheme.text, maxRotation: 0, autoSkip: true }, grid: { color: 'transparent' } },
				y: { stacked: true, beginAtZero: true, ticks: { color: tmpTheme.text, precision: 0 }, grid: { color: tmpTheme.grid } },
			},
		};
	}

	_createChart(pID, pType, pData, pOptions)
	{
		const tmpProvider = this.chartProvider;
		if (tmpProvider && typeof tmpProvider.createChart === 'function') { return tmpProvider.createChart(pID, pType, pData, pOptions); }
		return null;
	}

	_destroyChart(pID)
	{
		const tmpProvider = this.chartProvider;
		if (tmpProvider && typeof tmpProvider.destroyChart === 'function') { tmpProvider.destroyChart(pID); }
	}

	// ---- small formatters ---------------------------------------------------------------------------

	_num(pNum) { return (pNum == null || isNaN(pNum)) ? '0' : Number(pNum).toLocaleString(); }


	_relative(pDateStr)
	{
		if (!pDateStr) { return 'never'; }
		const tmpThen = new Date(pDateStr).getTime();
		if (isNaN(tmpThen)) { return '—'; }
		const tmpDiff = Date.now() - tmpThen;
		const tmpDay = 86400000;
		if (tmpDiff < 0) { return 'just now'; }
		if (tmpDiff < 3600000) { return `${Math.max(1, Math.round(tmpDiff / 60000))} min ago`; }
		if (tmpDiff < tmpDay) { return `${Math.round(tmpDiff / 3600000)} hr ago`; }
		if (tmpDiff < 30 * tmpDay) { return `${Math.round(tmpDiff / tmpDay)} days ago`; }
		if (tmpDiff < 365 * tmpDay) { return `${Math.round(tmpDiff / (30 * tmpDay))} mo ago`; }
		return `${Math.round(tmpDiff / (365 * tmpDay))} yr ago`;
	}

	async _resolveUserNamesAsync(pIDs)
	{
		const tmpMissing = pIDs.filter((pID) => pID && !this._userNameCache[pID]);
		if (!tmpMissing.length) { return; }
		const tmpUnique = Array.from(new Set(tmpMissing)).slice(0, 200);
		try
		{
			const tmpRows = await this._getJSON(`Users/FilteredTo/FBL~IDUser~INN~${tmpUnique.join(',')}/0/${tmpUnique.length}`);
			(Array.isArray(tmpRows) ? tmpRows : []).forEach((pUser) => { this._userNameCache[pUser.IDUser] = libUserSearch.userLabel(pUser); });
		}
		catch (pError) { /* leave ids unresolved -> shown as User #id */ }
	}

	_kpiTile(pLabel, pValue, pAccent)
	{
		return `<div class="ao-kpi"${pAccent ? ` style="border-top-color:${pAccent}"` : ''}><div class="ao-kpi-value">${this._esc(pValue)}</div><div class="ao-kpi-label">${this._esc(pLabel)}</div></div>`;
	}

	_windowBarHTML(pProviderMethod, pState, pWithMetric)
	{
		const fBtn = (pGroup, pVal, pLabel, pCurrent) => `<button type="button" class="ao-seg${(pCurrent === pVal) ? ' is-active' : ''}" onclick="_Pict.providers.ActivityObservability.${pProviderMethod}('${pGroup}','${pVal}')">${pLabel}</button>`;
		let tmpHTML = '<div class="ao-controls">';
		tmpHTML += '<span class="ao-control-label">Window</span><div class="ao-seggroup">'
			+ fBtn('window', '7d', '7d', pState.Window) + fBtn('window', '30d', '30d', pState.Window) + fBtn('window', '90d', '90d', pState.Window) + fBtn('window', '12mo', '12mo', pState.Window) + '</div>';
		tmpHTML += '<span class="ao-control-label">Bucket</span><div class="ao-seggroup">'
			+ fBtn('gran', 'auto', 'Auto', pState.Granularity) + fBtn('gran', 'day', 'Day', pState.Granularity) + fBtn('gran', 'week', 'Week', pState.Granularity) + fBtn('gran', 'month', 'Month', pState.Granularity) + '</div>';
		if (pWithMetric)
		{
			tmpHTML += '<span class="ao-control-label">Metric</span><div class="ao-seggroup">'
				+ fBtn('metric', 'create', 'Created', pState.Metric) + fBtn('metric', 'update', 'Updated', pState.Metric) + '</div>';
		}
		tmpHTML += '</div>';
		return tmpHTML;
	}

	// =================================================================================================
	// VIEW 1 — USER ACTIVITY
	// =================================================================================================

	static get USER_ENTITIES()
	{
		return [
			{ plural: 'Observations', label: 'Observations', color: '#ef4444' },
			{ plural: 'Documents', label: 'Documents', color: '#3b82f6' },
			{ plural: 'Comments', label: 'Comments', color: '#22c55e' },
			{ plural: 'DocumentApprovalLogs', label: 'Approvals', color: '#f59e0b' },
		];
	}

	/** Entry point from the route: load activity for a user (defaults to the signed-in user). */
	async loadUserActivityAsync(pIDUser)
	{
		this._ensureStyles();
		const tmpID = pIDUser ? Number(pIDUser) : this.sessionUserID();
		this.userActivity.IDUser = tmpID;
		// Resolve the user's display record (best-effort).
		if (tmpID)
		{
			try { const tmpRows = await this._getJSON(`Users/FilteredTo/FBV~IDUser~EQ~${tmpID}/0/1`); this.userActivity.User = (Array.isArray(tmpRows) && tmpRows[0]) || null; }
			catch (pError) { this.userActivity.User = null; }
		}
		await this._refreshUserActivityAsync();
	}

	setUserActivityOption(pGroup, pValue)
	{
		const tmpState = this.userActivity;
		if (pGroup === 'window') { tmpState.Window = pValue; }
		else if (pGroup === 'gran') { tmpState.Granularity = pValue; }
		else if (pGroup === 'metric') { tmpState.Metric = pValue; }
		this._refreshUserActivityAsync();
	}

	async _refreshUserActivityAsync()
	{
		const tmpState = this.userActivity;
		if (!tmpState.IDUser) { this.renderUserActivity(); return; }
		const tmpToken = ++tmpState.Token;
		const tmpBuckets = this._buildBuckets(tmpState.Window, tmpState.Granularity);
		const tmpEntities = ActivityObservabilityProvider.USER_ENTITIES;
		tmpState.Loading = true;
		tmpState.Error = '';
		tmpState.Buckets = tmpBuckets;
		// Pre-seed progress so the very first paint already shows the per-record-type checklist.
		tmpState.Progress = { active: true, done: 0, total: tmpEntities.length * tmpBuckets.length,
			perEntity: tmpEntities.map((pEntity) => ({ label: pEntity.label, color: pEntity.color, done: 0, total: tmpBuckets.length })) };
		this.renderUserActivity();
		try
		{
			const tmpDateField = (tmpState.Metric === 'update') ? 'UpdateDate' : 'CreateDate';
			const tmpActorField = (tmpState.Metric === 'update') ? 'UpdatingIDUser' : 'CreatingIDUser';
			const tmpBase = `FBV~${tmpActorField}~EQ~${tmpState.IDUser}`;
			const tmpTitle = `Counting ${libUserSearch.userLabel(tmpState.User) || 'this user'}’s activity…`;
			const fTick = () => { if (tmpToken === tmpState.Token) { this._assign('#AO-UserActivity-Progress', this._progressHTML(tmpState.Progress, tmpTitle)); } };
			const tmpSeries = await this._countAllSeriesAsync(tmpEntities, tmpBase, tmpDateField, tmpBuckets, tmpState.Progress, fTick);
			if (tmpToken !== tmpState.Token) { return; } // superseded
			tmpState.Series = tmpSeries;
			tmpState.Totals = {};
			tmpSeries.forEach((pSeries) => { tmpState.Totals[pSeries.label] = pSeries.total; });
			tmpState.Loading = false;
			this.renderUserActivity();
			this._drawUserActivityChart();
		}
		catch (pError)
		{
			if (tmpToken !== tmpState.Token) { return; }
			tmpState.Loading = false;
			tmpState.Error = `Could not load activity: ${(pError && pError.message) ? pError.message : pError}`;
			this.renderUserActivity();
		}
	}

	renderUserActivity()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement('#AO-UserActivity-Body').length === 0) { return; }
		this._assign('#AO-UserActivity-Body', this._userActivityHTML());
	}

	_userActivityHTML()
	{
		const tmpState = this.userActivity;
		let tmpHTML = '';
		// user picker header
		tmpHTML += '<div class="ao-pickrow">';
		tmpHTML += `<div class="ao-pick-current"><span class="ao-pick-icon"><i class="fas fa-user"></i></span><span class="ao-pick-name">${this._esc(libUserSearch.userLabel(tmpState.User) || 'Select a user')}</span>`;
		if (tmpState.User && tmpState.User.LoginID) { tmpHTML += `<span class="ao-pick-sub">${this._esc(tmpState.User.LoginID)}</span>`; }
		tmpHTML += `</div><button type="button" class="ao-pick-btn" onclick="_Pict.providers.ActivityObservability.toggleUserPicker()"><i class="fas fa-user-group"></i> Change user</button></div>`;
		if (tmpState.PickerOpen) { tmpHTML += this._userPickerHTML(); }

		tmpHTML += this._windowBarHTML('setUserActivityOption', tmpState, true);

		if (!tmpState.IDUser) { return tmpHTML + '<div class="ao-note">Pick a user to see their activity.</div>'; }
		if (tmpState.Error) { return tmpHTML + `<div class="ao-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(tmpState.Error)}</div>`; }

		// KPI tiles
		tmpHTML += '<div class="ao-kpis">';
		ActivityObservabilityProvider.USER_ENTITIES.forEach((pEntity) =>
		{
			tmpHTML += this._kpiTile(pEntity.label, tmpState.Loading ? '…' : this._num(tmpState.Totals[pEntity.label] || 0), pEntity.color);
		});
		tmpHTML += '</div>';

		if (tmpState.Loading)
		{
			const tmpTitle = `Counting ${libUserSearch.userLabel(tmpState.User) || 'this user'}’s activity…`;
			return tmpHTML + `<div id="AO-UserActivity-Progress">${this._progressHTML(tmpState.Progress, tmpTitle)}</div>`;
		}
		const tmpMetricWord = (tmpState.Metric === 'update') ? 'updated' : 'created';
		tmpHTML += `<div class="ao-chart-head"><span>Records ${tmpMetricWord} per ${this._activeGranularity || 'bucket'}</span></div>`;
		tmpHTML += '<div class="ao-chart-wrap"><canvas id="AO-UserActivity-Chart"></canvas></div>';
		return tmpHTML;
	}

	_userPickerHTML()
	{
		const tmpState = this.userActivity;
		let tmpHTML = '<div class="ao-picker">';
		tmpHTML += `<input class="ao-filter" type="text" autocomplete="off" placeholder="Search users by name, login ID or email…" value="${this._esc(tmpState.PickerSearch)}" oninput="_Pict.providers.ActivityObservability.onUserPickerSearch(this.value)" />`;
		tmpHTML += `<div class="ao-picker-list" id="AO-User-Picker-List">${this._userPickerListHTML()}</div>`;
		tmpHTML += '</div>';
		return tmpHTML;
	}

	/** Inner content of the user-picker results list. Assigned on its own (#AO-User-Picker-List) so the
	 *  sibling search <input> is never re-rendered and keeps focus/caret while typing. */
	_userPickerListHTML()
	{
		const tmpState = this.userActivity;
		let tmpHTML = '';
		if (tmpState.PickerSearching) { tmpHTML += '<div class="ao-note"><i class="fas fa-spinner fa-spin"></i> Searching…</div>'; }
		else if ((tmpState.PickerSearch || '').trim().length < 2) { tmpHTML += '<div class="ao-note">Type at least 2 characters, or pick from recent below.</div>'; }
		else if (!tmpState.PickerResults.length) { tmpHTML += '<div class="ao-note">No users match.</div>'; }
		tmpState.PickerResults.forEach((pUser) =>
		{
			tmpHTML += `<div class="ao-picker-row" onclick="_Pict.providers.ActivityObservability.selectUser(${Number(pUser.IDUser)})"><span>${this._esc(libUserSearch.userLabel(pUser))}</span><span class="ao-picker-sub">${this._esc(pUser.LoginID || pUser.Email || '')}</span></div>`;
		});
		return tmpHTML;
	}

	toggleUserPicker()
	{
		this.userActivity.PickerOpen = !this.userActivity.PickerOpen;
		this.renderUserActivity();
	}

	onUserPickerSearch(pValue)
	{
		this.userActivity.PickerSearch = pValue;
		if (this._pickerTimer) { clearTimeout(this._pickerTimer); }
		this._pickerTimer = setTimeout(() => { this._pickerTimer = null; this._userPickerSearchAsync(); }, 250);
	}

	async _userPickerSearchAsync()
	{
		const tmpState = this.userActivity;
		const tmpTerm = String(tmpState.PickerSearch || '').trim();
		const tmpToken = ++tmpState.Token;
		if (tmpTerm.length < 2)
		{
			tmpState.PickerResults = [];
			tmpState.PickerSearching = false;
			this._assignUserPicker();
			return;
		}
		tmpState.PickerSearching = true;
		this._assignUserPicker();
		let tmpUsers = [];
		try
		{
			tmpUsers = await libUserSearch.searchUsersAsync((pURL) => this._getJSON(pURL), tmpTerm, { Scope: `FBV~IDCustomer~EQ~${this.callerCustomer()}~FBV~Deleted~EQ~0`, PageSize: 40 });
		}
		catch (pError)
		{
			tmpUsers = [];
		}
		if (tmpToken !== tmpState.Token)
		{
			return;
		}
		tmpState.PickerResults = tmpUsers;
		tmpState.PickerSearching = false;
		this._assignUserPicker();
	}

	_assignUserPicker()
	{
		// Repaint ONLY the results list, never the <input> sibling — a full body re-render would destroy
		// the focused search box and drop the caret after each keystroke (mirrors the project picker fix).
		this._assign('#AO-User-Picker-List', this._userPickerListHTML());
	}

	async selectUser(pIDUser)
	{
		this.userActivity.PickerOpen = false;
		this.userActivity.PickerSearch = '';
		this.userActivity.PickerResults = [];
		if (window && window.location) { window.location.hash = `#/UserActivity/${Number(pIDUser)}`; }
		await this.loadUserActivityAsync(pIDUser);
	}

	_drawUserActivityChart()
	{
		const tmpState = this.userActivity;
		if (!tmpState.Series.length) { this._destroyChart('AO-UserActivity-Chart'); return; }
		const tmpData =
		{
			labels: tmpState.Buckets.map((pBucket) => pBucket.label),
			datasets: tmpState.Series.map((pSeries) => ({ label: pSeries.label, data: pSeries.counts, backgroundColor: pSeries.color, borderWidth: 0, borderRadius: 2 })),
		};
		this._createChart('AO-UserActivity-Chart', 'bar', tmpData, this._stackedBarOptions());
	}

	// =================================================================================================
	// VIEW 2 — USER LOGINS
	// =================================================================================================

	async loadLoginsAsync()
	{
		this._ensureStyles();
		const tmpState = this.logins;
		const tmpToken = ++tmpState.Token;
		tmpState.Loading = true;
		tmpState.Error = '';
		this.renderLogins();
		const tmpCustomer = this.callerCustomer();
		const tmpNow = Date.now();
		const tmpISO = (pMsAgo) => new Date(tmpNow - pMsAgo).toISOString().slice(0, 19);
		const tmp7 = tmpISO(7 * 86400000);
		const tmp30 = tmpISO(30 * 86400000);
		const tmpBase = `FBV~IDCustomer~EQ~${tmpCustomer}~FBV~Deleted~EQ~0`;
		try
		{
			const tmpResults = await Promise.all([
				this._getJSON(`Users/FilteredTo/${tmpBase}~FSF~LastLoginTime~DESC~0/0/300`),
				this._countAsync('Users', tmpBase),
				this._countAsync('Users', `${tmpBase}~FBV~LastLoginTime~GE~${tmp7}`),
				this._countAsync('Users', `${tmpBase}~FBV~LastLoginTime~GE~${tmp30}~FBV~LastLoginTime~LT~${tmp7}`),
				this._countAsync('Users', `${tmpBase}~FBV~LastLoginTime~LT~${tmp30}`),
				this._countAsync('Users', `${tmpBase}~FBV~LastLoginTime~IN~`),
			]);
			if (tmpToken !== tmpState.Token) { return; }
			tmpState.Users = Array.isArray(tmpResults[0]) ? tmpResults[0] : [];
			tmpState.Total = tmpResults[1];
			tmpState.Recency = { active: tmpResults[2], idle: tmpResults[3], dormant: tmpResults[4], never: tmpResults[5] };
			tmpState.Loading = false;
			this.renderLogins();
			this._drawLoginDoughnut();
		}
		catch (pError)
		{
			if (tmpToken !== tmpState.Token) { return; }
			tmpState.Loading = false;
			tmpState.Error = `Could not load users: ${(pError && pError.message) ? pError.message : pError}`;
			this.renderLogins();
		}
	}

	_recencyOf(pUser)
	{
		if (!pUser.LastLoginTime) { return { key: 'never', label: 'Never', cls: 'ao-badge-never' }; }
		const tmpDays = (Date.now() - new Date(pUser.LastLoginTime).getTime()) / 86400000;
		if (tmpDays < 7) { return { key: 'active', label: 'Active', cls: 'ao-badge-active' }; }
		if (tmpDays < 30) { return { key: 'idle', label: 'Idle', cls: 'ao-badge-idle' }; }
		return { key: 'dormant', label: 'Dormant', cls: 'ao-badge-dormant' };
	}

	renderLogins()
	{
		if (this.pict.ContentAssignment.getElement('#AO-Logins-Body').length === 0) { return; }
		this._assign('#AO-Logins-Body', this._loginsHTML());
	}

	_loginsHTML()
	{
		const tmpState = this.logins;
		if (tmpState.Loading && !tmpState.Users.length) { return '<div class="ao-note"><i class="fas fa-spinner fa-spin"></i> Loading users…</div>'; }
		if (tmpState.Error) { return `<div class="ao-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(tmpState.Error)}</div>`; }
		const tmpRec = tmpState.Recency;
		let tmpHTML = '';
		tmpHTML += '<div class="ao-kpis">';
		tmpHTML += this._kpiTile('Total users', this._num(tmpState.Total), '#64748b');
		tmpHTML += this._kpiTile('Active (7d)', this._num(tmpRec.active), '#22c55e');
		tmpHTML += this._kpiTile('Dormant (30d+)', this._num(tmpRec.dormant), '#f59e0b');
		tmpHTML += this._kpiTile('Never logged in', this._num(tmpRec.never), '#ef4444');
		tmpHTML += '</div>';

		tmpHTML += '<div class="ao-split">';
		tmpHTML += '<div class="ao-split-chart"><div class="ao-chart-head"><span>Login recency</span></div><div class="ao-chart-wrap ao-chart-sm"><canvas id="AO-Logins-Doughnut"></canvas></div></div>';
		tmpHTML += '<div class="ao-split-table">';
		tmpHTML += `<input id="AO-Logins-Filter" class="ao-filter" type="text" autocomplete="off" placeholder="Filter the ${this._num(tmpState.Users.length)} most-recent by name or login…" oninput="_Pict.providers.ActivityObservability.onLoginFilter(this.value)" />`;
		tmpHTML += `<div id="AO-Logins-List" class="ao-tablewrap">${this._loginRowsHTML()}</div>`;
		if (tmpState.Total > tmpState.Users.length) { tmpHTML += `<div class="ao-note ao-note-sm">Showing the ${this._num(tmpState.Users.length)} most-recently-active of ${this._num(tmpState.Total)} users. The recency tiles &amp; chart above count all users.</div>`; }
		tmpHTML += '</div></div>';
		return tmpHTML;
	}

	_loginRowsHTML()
	{
		const tmpState = this.logins;
		const tmpTerm = String(tmpState.Filter || '').trim().toLowerCase();
		const tmpRows = tmpState.Users.filter((pUser) => !tmpTerm
			|| libUserSearch.userLabel(pUser).toLowerCase().indexOf(tmpTerm) > -1
			|| String(pUser.LoginID || '').toLowerCase().indexOf(tmpTerm) > -1);
		if (!tmpRows.length) { return '<div class="ao-note">No users match your filter.</div>'; }
		let tmpHTML = '<table class="ao-table"><thead><tr><th>User</th><th>Login</th><th>Last login</th><th>Status</th></tr></thead><tbody>';
		tmpRows.forEach((pUser) =>
		{
			const tmpRec = this._recencyOf(pUser);
			tmpHTML += `<tr class="ao-trow" onclick="_Pict.providers.ActivityObservability.openUserActivity(${Number(pUser.IDUser)})">`
				+ `<td>${this._esc(libUserSearch.userLabel(pUser))}</td>`
				+ `<td class="ao-mono">${this._esc(pUser.LoginID || '')}</td>`
				+ `<td>${pUser.LastLoginTime ? this._esc(this._relative(pUser.LastLoginTime)) : '<span class="ao-muted">never</span>'}</td>`
				+ `<td><span class="ao-badge ${tmpRec.cls}">${tmpRec.label}</span></td></tr>`;
		});
		tmpHTML += '</tbody></table>';
		return tmpHTML;
	}

	onLoginFilter(pValue)
	{
		this.logins.Filter = pValue;
		this._assign('#AO-Logins-List', this._loginRowsHTML());
	}

	openUserActivity(pIDUser)
	{
		if (window && window.location) { window.location.hash = `#/UserActivity/${Number(pIDUser)}`; }
	}

	_drawLoginDoughnut()
	{
		const tmpRec = this.logins.Recency;
		const tmpTheme = this._themeColors();
		const tmpData =
		{
			labels: [ 'Active (<7d)', 'Idle (7–30d)', 'Dormant (30d+)', 'Never' ],
			datasets: [ { data: [ tmpRec.active, tmpRec.idle, tmpRec.dormant, tmpRec.never ], backgroundColor: [ '#22c55e', '#3b82f6', '#f59e0b', '#ef4444' ], borderWidth: 0 } ],
		};
		this._createChart('AO-Logins-Doughnut', 'doughnut', tmpData, { cutout: '62%', plugins: { legend: { position: 'bottom', labels: { color: tmpTheme.text, usePointStyle: true, boxWidth: 10 } } } });
	}

	// =================================================================================================
	// VIEW 3 — PROJECT ACTIVITY
	// =================================================================================================

	static get PROJECT_ENTITIES()
	{
		return [
			{ plural: 'Documents', label: 'Documents', color: '#3b82f6' },
			{ plural: 'Observations', label: 'Observations', color: '#ef4444' },
			{ plural: 'LineItems', label: 'Line Items', color: '#8b5cf6' },
		];
	}

	async loadProjectActivityAsync(pIDProject)
	{
		this._ensureStyles();
		const tmpState = this.projectActivity;
		if (pIDProject) { tmpState.IDProject = Number(pIDProject); }
		if (tmpState.IDProject)
		{
			try { const tmpRows = await this._getJSON(`Projects/FilteredTo/FBV~IDProject~EQ~${tmpState.IDProject}/0/1`); tmpState.Project = (Array.isArray(tmpRows) && tmpRows[0]) || null; }
			catch (pError) { tmpState.Project = null; }
		}
		await this._refreshProjectActivityAsync();
	}

	setProjectActivityOption(pGroup, pValue)
	{
		const tmpState = this.projectActivity;
		if (pGroup === 'window') { tmpState.Window = pValue; }
		else if (pGroup === 'gran') { tmpState.Granularity = pValue; }
		else if (pGroup === 'metric') { tmpState.Metric = pValue; }
		this._refreshProjectActivityAsync();
	}

	async _refreshProjectActivityAsync()
	{
		const tmpState = this.projectActivity;
		if (!tmpState.IDProject) { this.renderProjectActivity(); return; }
		const tmpToken = ++tmpState.Token;
		const tmpBuckets = this._buildBuckets(tmpState.Window, tmpState.Granularity);
		const tmpEntities = ActivityObservabilityProvider.PROJECT_ENTITIES;
		tmpState.Loading = true;
		tmpState.Error = '';
		tmpState.Buckets = tmpBuckets;
		tmpState.Progress = { active: true, done: 0, total: tmpEntities.length * tmpBuckets.length,
			perEntity: tmpEntities.map((pEntity) => ({ label: pEntity.label, color: pEntity.color, done: 0, total: tmpBuckets.length })) };
		this.renderProjectActivity();
		try
		{
			const tmpDateField = (tmpState.Metric === 'update') ? 'UpdateDate' : 'CreateDate';
			const tmpBase = `FBV~IDProject~EQ~${tmpState.IDProject}`;
			const tmpTitle = `Counting activity for ${(tmpState.Project && tmpState.Project.Name) || 'this project'}…`;
			const fTick = () => { if (tmpToken === tmpState.Token) { this._assign('#AO-Project-Progress', this._progressHTML(tmpState.Progress, tmpTitle)); } };
			const tmpSeries = await this._countAllSeriesAsync(tmpEntities, tmpBase, tmpDateField, tmpBuckets, tmpState.Progress, fTick);
			if (tmpToken !== tmpState.Token) { return; }
			tmpState.Series = tmpSeries;
			tmpState.Totals = {};
			tmpSeries.forEach((pSeries) => { tmpState.Totals[pSeries.label] = pSeries.total; });
			tmpState.Loading = false;
			this.renderProjectActivity();
			this._drawProjectChart();
			this._loadProjectStreamAsync(tmpToken);
		}
		catch (pError)
		{
			if (tmpToken !== tmpState.Token) { return; }
			tmpState.Loading = false;
			tmpState.Error = `Could not load activity: ${(pError && pError.message) ? pError.message : pError}`;
			this.renderProjectActivity();
		}
	}

	/** Bounded tail read per entity -> a merged, most-recent-first activity stream (live rows only). */
	async _loadProjectStreamAsync(pToken)
	{
		const tmpState = this.projectActivity;
		tmpState.StreamLoading = true;
		this._assign('#AO-Project-Stream', '<div class="ao-note"><i class="fas fa-spinner fa-spin"></i> Loading recent activity…</div>');
		try
		{
			const tmpPer = await Promise.all(ActivityObservabilityProvider.PROJECT_ENTITIES.map(async (pEntity) =>
			{
				const tmpRows = await this._getJSON(`${pEntity.plural}/FilteredTo/FBV~IDProject~EQ~${tmpState.IDProject}~FBV~Deleted~EQ~0~FSF~UpdateDate~DESC~0/0/15`).catch(() => []);
				return (Array.isArray(tmpRows) ? tmpRows : []).map((pRow) => ({
					type: pEntity.label, color: pEntity.color,
					name: pRow.Name || pRow.Description || `#${pRow.IDDocument || pRow.IDObservation || pRow.IDLineItem || ''}`,
					created: pRow.CreateDate, updated: pRow.UpdateDate,
					actor: pRow.UpdatingIDUser || pRow.CreatingIDUser,
					isNew: (pRow.CreateDate === pRow.UpdateDate),
				}));
			}));
			if (pToken !== tmpState.Token) { return; }
			const tmpMerged = [].concat.apply([], tmpPer)
				.sort((pA, pB) => new Date(pB.updated || pB.created).getTime() - new Date(pA.updated || pA.created).getTime())
				.slice(0, 40);
			await this._resolveUserNamesAsync(tmpMerged.map((pItem) => pItem.actor));
			if (pToken !== tmpState.Token) { return; }
			tmpState.Stream = tmpMerged;
			tmpState.StreamLoading = false;
			this._assign('#AO-Project-Stream', this._streamHTML());
		}
		catch (pError)
		{
			if (pToken !== tmpState.Token) { return; }
			tmpState.StreamLoading = false;
			this._assign('#AO-Project-Stream', `<div class="ao-error">${this._esc((pError && pError.message) || pError)}</div>`);
		}
	}

	renderProjectActivity()
	{
		if (this.pict.ContentAssignment.getElement('#AO-Project-Body').length === 0) { return; }
		this._assign('#AO-Project-Body', this._projectActivityHTML());
	}

	_projectActivityHTML()
	{
		const tmpState = this.projectActivity;
		let tmpHTML = '';
		tmpHTML += '<div class="ao-pickrow">';
		tmpHTML += `<div class="ao-pick-current"><span class="ao-pick-icon"><i class="fas fa-folder-open"></i></span><span class="ao-pick-name">${this._esc((tmpState.Project && tmpState.Project.Name) || 'Select a project')}</span></div>`;
		tmpHTML += `<button type="button" class="ao-pick-btn" onclick="_Pict.providers.ActivityObservability.toggleProjectPicker()"><i class="fas fa-folder-tree"></i> Change project</button></div>`;
		if (tmpState.PickerOpen) { tmpHTML += this._projectPickerHTML(); }

		tmpHTML += this._windowBarHTML('setProjectActivityOption', tmpState, true);

		if (!tmpState.IDProject) { return tmpHTML + '<div class="ao-note">Pick a project to see its activity.</div>'; }
		if (tmpState.Error) { return tmpHTML + `<div class="ao-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(tmpState.Error)}</div>`; }

		tmpHTML += '<div class="ao-kpis">';
		ActivityObservabilityProvider.PROJECT_ENTITIES.forEach((pEntity) =>
		{
			tmpHTML += this._kpiTile(pEntity.label, tmpState.Loading ? '…' : this._num(tmpState.Totals[pEntity.label] || 0), pEntity.color);
		});
		tmpHTML += '</div>';

		if (tmpState.Loading)
		{
			const tmpTitle = `Counting activity for ${(tmpState.Project && tmpState.Project.Name) || 'this project'}…`;
			return tmpHTML + `<div id="AO-Project-Progress">${this._progressHTML(tmpState.Progress, tmpTitle)}</div>`;
		}
		const tmpMetricWord = (tmpState.Metric === 'update') ? 'updated' : 'created';
		tmpHTML += `<div class="ao-chart-head"><span>Records ${tmpMetricWord} per ${this._activeGranularity || 'bucket'}</span></div>`;
		tmpHTML += '<div class="ao-chart-wrap"><canvas id="AO-Project-Chart"></canvas></div>';
		tmpHTML += '<div class="ao-chart-head"><span>Recent activity</span></div>';
		tmpHTML += `<div id="AO-Project-Stream">${this._streamHTML()}</div>`;
		return tmpHTML;
	}

	_streamHTML()
	{
		const tmpState = this.projectActivity;
		if (tmpState.StreamLoading) { return '<div class="ao-note"><i class="fas fa-spinner fa-spin"></i> Loading recent activity…</div>'; }
		if (!tmpState.Stream.length) { return '<div class="ao-note">No recent records.</div>'; }
		let tmpHTML = '<ul class="ao-stream">';
		tmpState.Stream.forEach((pItem) =>
		{
			const tmpWhen = pItem.updated || pItem.created;
			const tmpActor = this._userNameCache[pItem.actor] || (pItem.actor ? `User #${pItem.actor}` : 'unknown');
			tmpHTML += `<li class="ao-stream-item"><span class="ao-stream-dot" style="background:${pItem.color}"></span>`
				+ `<span class="ao-stream-type">${this._esc(pItem.type)}</span>`
				+ `<span class="ao-stream-name">${this._esc(pItem.name)}</span>`
				+ `<span class="ao-stream-meta">${pItem.isNew ? 'created' : 'updated'} by ${this._esc(tmpActor)} · ${this._esc(this._relative(tmpWhen))}</span></li>`;
		});
		tmpHTML += '</ul>';
		return tmpHTML;
	}

	toggleProjectPicker()
	{
		const tmpState = this.projectActivity;
		tmpState.PickerOpen = !tmpState.PickerOpen;
		this.renderProjectActivity();
		// Browse-on-open: show the first page of the caller's projects immediately instead of an empty
		// "type to search" box. The server scopes Project reads to what this user can actually reach
		// (member + soft-membership for regular users; the whole customer for admins), name-ordered — so
		// this lists the projects they have access to, and paging (below) reaches all of them, not just 40.
		if (tmpState.PickerOpen) { tmpState.PickerPage = 0; this._projectPickerSearchAsync(false); }
	}

	onProjectPickerSearch(pValue)
	{
		this.projectActivity.PickerSearch = pValue;
		this.projectActivity.PickerPage = 0;
		if (this._pickerTimer) { clearTimeout(this._pickerTimer); }
		this._pickerTimer = setTimeout(() => { this._pickerTimer = null; this._projectPickerSearchAsync(false); }, 250);
	}

	/** Append the next page of picker results (the "Load more projects…" row). */
	loadMoreProjects()
	{
		this.projectActivity.PickerPage = (this.projectActivity.PickerPage || 0) + 1;
		this._projectPickerSearchAsync(true);
	}

	async _projectPickerSearchAsync(pAppend)
	{
		const tmpState = this.projectActivity;
		const tmpTerm = String(tmpState.PickerSearch || '').replace(/~/g, '').trim();
		const tmpToken = ++tmpState.Token;
		const tmpPageSize = 100;
		if (!pAppend) { tmpState.PickerPage = 0; tmpState.PickerResults = []; }
		tmpState.PickerSearching = true;
		// Repaint ONLY the results list, never the <input> sibling: re-rendering the whole body would
		// destroy the focused search box and drop the caret after every keystroke.
		this._assignProjectPickerList();
		// Keep the IDCustomer scope (safe — the server also scopes Project reads to what this user can
		// reach). Fall back to the server scope alone when the session carries no customer id, since a
		// bare `IDCustomer~EQ~` (empty) matches nothing.
		const tmpCustomer = this.callerCustomer();
		let tmpFilter = tmpCustomer ? `FBV~IDCustomer~EQ~${tmpCustomer}~FBV~Deleted~EQ~0` : 'FBV~Deleted~EQ~0';
		if (tmpTerm.length >= 2) { tmpFilter += `~FBV~Name~LK~${encodeURIComponent('%' + tmpTerm.replace(/\*/g, '%') + '%')}`; }
		const tmpStart = (tmpState.PickerPage || 0) * tmpPageSize;
		try
		{
			const tmpRows = await this._getJSON(`Projects/Lite/FilteredTo/${tmpFilter}~FSF~Name~ASC~0/${tmpStart}/${tmpPageSize}`);
			if (tmpToken !== tmpState.Token) { return; }
			const tmpMapped = (Array.isArray(tmpRows) ? tmpRows : []).map((pRow) => ({ IDProject: pRow.ID || pRow.IDProject, Name: pRow.Value || pRow.Name || `Project #${pRow.ID || pRow.IDProject}` }));
			tmpState.PickerResults = pAppend ? tmpState.PickerResults.concat(tmpMapped) : tmpMapped;
			tmpState.PickerHasMore = (Array.isArray(tmpRows) && tmpRows.length >= tmpPageSize);
			tmpState.PickerSearching = false;
			this._assignProjectPickerList();
		}
		catch (pError)
		{
			if (tmpToken !== tmpState.Token) { return; }
			tmpState.PickerSearching = false; if (!pAppend) { tmpState.PickerResults = []; }
			this._assignProjectPickerList();
		}
	}

	_projectPickerHTML()
	{
		const tmpState = this.projectActivity;
		let tmpHTML = '<div class="ao-picker">';
		tmpHTML += `<input class="ao-filter" type="text" autocomplete="off" placeholder="Search projects by name…" value="${this._esc(tmpState.PickerSearch)}" oninput="_Pict.providers.ActivityObservability.onProjectPickerSearch(this.value)" />`;
		tmpHTML += `<div class="ao-picker-list" id="AO-Project-Picker-List">${this._projectPickerListHTML()}</div>`;
		tmpHTML += '</div>';
		return tmpHTML;
	}

	/** Inner content of the picker results list (spinner / empty / rows / load-more). Assigned on its own
	 *  (#AO-Project-Picker-List) so the sibling search <input> is never re-rendered and keeps focus/caret. */
	_projectPickerListHTML()
	{
		const tmpState = this.projectActivity;
		let tmpHTML = '';
		if (tmpState.PickerSearching && !tmpState.PickerResults.length) { tmpHTML += '<div class="ao-note"><i class="fas fa-spinner fa-spin"></i> Searching…</div>'; }
		else if (!tmpState.PickerResults.length) { tmpHTML += '<div class="ao-note">No projects found.</div>'; }
		tmpState.PickerResults.forEach((pProject) =>
		{
			tmpHTML += `<div class="ao-picker-row" onclick="_Pict.providers.ActivityObservability.selectProject(${Number(pProject.IDProject)})"><span>${this._esc(pProject.Name)}</span><span class="ao-picker-sub">#${Number(pProject.IDProject)}</span></div>`;
		});
		if (tmpState.PickerHasMore) { tmpHTML += `<div class="ao-picker-row ao-picker-more" onclick="_Pict.providers.ActivityObservability.loadMoreProjects()"><i class="fas fa-angle-down"></i>&nbsp;Load more projects…</div>`; }
		return tmpHTML;
	}

	_assignProjectPickerList()
	{
		this._assign('#AO-Project-Picker-List', this._projectPickerListHTML());
	}

	async selectProject(pIDProject)
	{
		this.projectActivity.PickerOpen = false;
		this.projectActivity.PickerSearch = '';
		this.projectActivity.PickerResults = [];
		if (window && window.location) { window.location.hash = `#/ProjectActivity/${Number(pIDProject)}`; }
		await this.loadProjectActivityAsync(pIDProject);
	}

	_drawProjectChart()
	{
		const tmpState = this.projectActivity;
		if (!tmpState.Series.length) { this._destroyChart('AO-Project-Chart'); return; }
		const tmpData =
		{
			labels: tmpState.Buckets.map((pBucket) => pBucket.label),
			datasets: tmpState.Series.map((pSeries) => ({ label: pSeries.label, data: pSeries.counts, backgroundColor: pSeries.color, borderWidth: 0, borderRadius: 2 })),
		};
		this._createChart('AO-Project-Chart', 'bar', tmpData, this._stackedBarOptions());
	}
}

ActivityObservabilityProvider.SHARED_CSS = /*css*/`
	.ao-wrap { padding: 1.25rem 1.5rem 2.5rem; }
	.ao-head { margin: 0 0 1rem; }
	.ao-title { font-size: 1.5rem; font-weight: 700; line-height: 1.2; margin: 0; color: var(--theme-color-text-primary,#1f2733); }
	.ao-subtitle { font-size: 0.9rem; color: var(--theme-color-text-muted,#6b7686); margin: 0.2rem 0 0; max-width: 62rem; }
	.ao-note { padding: 0.9rem; font-size: 0.9rem; color: var(--theme-color-text-muted,#8894a4); }
	.ao-note-sm { padding: 0.5rem 0.2rem; font-size: 0.8rem; }
	.ao-error { padding: 0.9rem; font-size: 0.88rem; color: var(--theme-color-status-danger,#c0392b); }
	.ao-muted { color: var(--theme-color-text-muted,#9aa4b2); font-style: italic; }
	.ao-mono { font-family: ui-monospace,SFMono-Regular,Menlo,monospace; font-size: 0.82rem; color: var(--theme-color-text-muted,#8a94a3); }
	.ao-pickrow { display: flex; align-items: center; justify-content: space-between; gap: 1rem; background: var(--theme-color-background-secondary,#f4f6f9); border: 1px solid var(--theme-color-border-light,#eef1f5); border-radius: 10px; padding: 0.7rem 0.95rem; margin: 0 0 0.9rem; }
	.ao-pick-current { display: flex; align-items: baseline; gap: 0.6rem; min-width: 0; }
	.ao-pick-icon { color: var(--theme-color-brand-primary,#156dd1); }
	.ao-pick-name { font-weight: 700; font-size: 1.05rem; color: var(--theme-color-text-primary,#1f2733); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.ao-pick-sub { font-size: 0.82rem; color: var(--theme-color-text-muted,#8a94a3); }
	.ao-pick-btn, .ao-seg { cursor: pointer; font-size: 0.82rem; font-weight: 600; border-radius: 7px; border: 1px solid var(--theme-color-border-medium,#d5dbe3); background: var(--theme-color-background-input,#fff); color: var(--theme-color-text-primary,#1f2733); padding: 0.4rem 0.75rem; }
	.ao-pick-btn:hover { border-color: var(--theme-color-brand-primary,#156dd1); }
	.ao-picker { border: 1px solid var(--theme-color-border-light,#eef1f5); border-radius: 10px; padding: 0.7rem; margin: 0 0 0.9rem; background: var(--theme-color-background-primary,#fff); }
	.ao-picker-list { max-height: 16rem; overflow-y: auto; margin-top: 0.5rem; }
	.ao-picker-row { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.45rem 0.6rem; border-radius: 6px; cursor: pointer; font-size: 0.9rem; }
	.ao-picker-row:hover { background: var(--theme-color-background-secondary,#f4f6f9); }
	.ao-picker-sub { color: var(--theme-color-text-muted,#8a94a3); font-size: 0.8rem; }
	.ao-picker-more { justify-content: center; color: var(--theme-color-primary,#2b6cb0); font-size: 0.82rem; border-top: 1px solid var(--theme-color-border-light,#eef1f5); margin-top: 0.25rem; }
	.ao-filter { width: 100%; box-sizing: border-box; font-size: 0.92rem; padding: 0.5rem 0.7rem; border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 8px; background: var(--theme-color-background-input,#fff); color: var(--theme-color-text-primary,#1f2733); }
	.ao-filter:focus { outline: none; border-color: var(--theme-color-brand-primary,#2f6fed); box-shadow: 0 0 0 2px rgba(47,111,237,0.15); }
	.ao-controls { display: flex; align-items: center; flex-wrap: wrap; gap: 0.5rem 0.75rem; margin: 0 0 1rem; }
	.ao-control-label { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted,#8a94a3); }
	.ao-seggroup { display: inline-flex; border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 8px; overflow: hidden; }
	.ao-seggroup .ao-seg { border: none; border-right: 1px solid var(--theme-color-border-light,#eef1f5); border-radius: 0; background: var(--theme-color-background-input,#fff); }
	.ao-seggroup .ao-seg:last-child { border-right: none; }
	.ao-seg.is-active { background: var(--theme-color-brand-primary,#156dd1); color: #fff; }
	.ao-kpis { display: grid; grid-template-columns: repeat(auto-fit,minmax(9rem,1fr)); gap: 0.75rem; margin: 0 0 1.1rem; }
	.ao-kpi { border: 1px solid var(--theme-color-border-light,#eef1f5); border-top: 3px solid var(--theme-color-brand-primary,#156dd1); border-radius: 10px; padding: 0.8rem 0.9rem; background: var(--theme-color-background-primary,#fff); }
	.ao-kpi-value { font-size: 1.6rem; font-weight: 700; line-height: 1.1; color: var(--theme-color-text-primary,#1f2733); }
	.ao-kpi-label { font-size: 0.78rem; color: var(--theme-color-text-muted,#8a94a3); margin-top: 0.2rem; }
	.ao-chart-head { display: flex; align-items: center; justify-content: space-between; font-size: 0.9rem; font-weight: 700; color: var(--theme-color-text-primary,#1f2733); margin: 0.6rem 0 0.5rem; }
	.ao-spin { font-size: 0.8rem; font-weight: 500; color: var(--theme-color-text-muted,#8a94a3); }
	.ao-chart-wrap { position: relative; height: 20rem; background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-light,#eef1f5); border-radius: 10px; padding: 0.75rem; }
	.ao-chart-sm { height: 15rem; }
	.ao-split { display: grid; grid-template-columns: minmax(14rem,20rem) 1fr; gap: 1.1rem; align-items: start; }
	@media (max-width: 820px) { .ao-split { grid-template-columns: 1fr; } }
	.ao-tablewrap { margin-top: 0.6rem; border: 1px solid var(--theme-color-border-light,#eef1f5); border-radius: 10px; }
	.ao-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
	.ao-table thead th { position: sticky; top: 0; background: var(--theme-color-background-secondary,#f4f6f9); text-align: left; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted,#8a94a3); padding: 0.5rem 0.75rem; border-bottom: 1px solid var(--theme-color-border-light,#eef1f5); }
	.ao-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid var(--theme-color-border-light,#eef1f5); color: var(--theme-color-text-primary,#1f2733); }
	.ao-trow { cursor: pointer; }
	.ao-trow:hover td { background: var(--theme-color-background-secondary,#f4f6f9); }
	.ao-badge { font-size: 0.68rem; font-weight: 700; padding: 0.1rem 0.5rem; border-radius: 9px; color: #fff; }
	.ao-badge-active { background: #22c55e; }
	.ao-badge-idle { background: #3b82f6; }
	.ao-badge-dormant { background: #f59e0b; }
	.ao-badge-never { background: #94a3b8; }
	.ao-stream { list-style: none; margin: 0.3rem 0 0; padding: 0; border: 1px solid var(--theme-color-border-light,#eef1f5); border-radius: 10px; overflow: hidden; }
	.ao-stream-item { display: flex; align-items: center; gap: 0.6rem; padding: 0.5rem 0.85rem; border-bottom: 1px solid var(--theme-color-border-light,#eef1f5); font-size: 0.9rem; }
	.ao-stream-item:last-child { border-bottom: none; }
	.ao-stream-dot { flex: 0 0 auto; width: 0.55rem; height: 0.55rem; border-radius: 50%; }
	.ao-stream-type { flex: 0 0 6rem; font-weight: 700; font-size: 0.78rem; color: var(--theme-color-text-secondary,#45505f); }
	.ao-stream-name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--theme-color-text-primary,#1f2733); }
	.ao-stream-meta { flex: 0 0 auto; font-size: 0.78rem; color: var(--theme-color-text-muted,#8a94a3); }
	.ao-progress { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; gap: 0.55rem; min-height: 20rem; padding: 2rem 1rem; background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-light,#eef1f5); border-radius: 10px; }
	.ao-progress-spin { font-size: 1.7rem; color: var(--theme-color-brand-primary,#156dd1); }
	.ao-progress-title { font-size: 1.05rem; font-weight: 700; color: var(--theme-color-text-primary,#1f2733); max-width: 34rem; }
	.ao-progress-sub { font-size: 0.85rem; color: var(--theme-color-text-muted,#8a94a3); font-variant-numeric: tabular-nums; }
	.ao-progress-bar { width: min(28rem,90%); height: 0.5rem; background: var(--theme-color-background-tertiary,#eef1f6); border-radius: 99px; overflow: hidden; margin-top: 0.15rem; }
	.ao-progress-fill { height: 100%; background: var(--theme-color-brand-primary,#156dd1); border-radius: 99px; transition: width 0.15s linear; }
	.ao-progress-entities { width: min(28rem,90%); margin-top: 0.6rem; display: flex; flex-direction: column; gap: 0.45rem; }
	.ao-progress-entity { display: flex; align-items: center; gap: 0.5rem; font-size: 0.85rem; }
	.ao-progress-dot { flex: 0 0 auto; width: 0.55rem; height: 0.55rem; border-radius: 50%; }
	.ao-progress-elabel { flex: 0 0 7rem; text-align: left; color: var(--theme-color-text-secondary,#45505f); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.ao-progress-etrack { flex: 1 1 auto; height: 0.35rem; background: var(--theme-color-background-tertiary,#eef1f6); border-radius: 99px; overflow: hidden; }
	.ao-progress-efill { display: block; height: 100%; border-radius: 99px; transition: width 0.15s linear; }
	.ao-progress-ecount { flex: 0 0 2.6rem; text-align: right; color: var(--theme-color-text-muted,#8a94a3); font-variant-numeric: tabular-nums; }
	.ao-progress-ecount .fa-check { color: var(--theme-color-status-success,#22c55e); }
`;

ActivityObservabilityProvider.default_configuration = { ProviderIdentifier: 'ActivityObservability' };

module.exports = ActivityObservabilityProvider;
module.exports.default_configuration = ActivityObservabilityProvider.default_configuration;
